import { Fragment, Suspense, useEffect } from "react";
import { useFragment } from "react-relay";
import {
	contentSubmissionEnd_ContentSubmissionFragment$key,
	RewardKind,
	RewardResultKind,
} from "@generated/contentSubmissionEnd_ContentSubmissionFragment.graphql";
import { CONTENT_SUBMISSION_FRAGMENT } from "./content-submission-end.graphql";
import {
	Congratulation,
	MessageContainer,
	Percentage,
	ResultStatusText,
	RewardLabel,
	RewardsContainer,
	StyledKnob,
} from "./content-submission-end.styles";
import { ContentSubmissionEndProps } from "./content-submission-end.types";
import { ModuleRating } from "./parts/module-rating";
import {
	formatDateTime,
	formatDateTimeAndAddDays,
} from "../../../infecto-lms-webapp/components/DateTimeDisplay.component";
import { isTemporarilyBlockedUntilUtil } from "../../../infecto-lms-webapp/utils/advancement-result/is-temporarily-blocked-until";
import { LMS_WEBAPP_CONFIG_IMPL } from "../../../infecto-lms-webapp-impl/config";
import { ProjectContentElementContainer } from "../../../infecto-lms-webapp-impl/containers/ProjectContentElement.container";
import { isPermanentlyBlockedUtil } from "../../utils/advancement-result/is-permanently-blocked";
import { GoToDashboardButton } from "../GoToDashboardButton";
import { ShowAnswerButtonContainer } from "../node-screen/parts/show-answer-button-container/show-answer-button.container";
import { DownloadIcon } from "../nodecard/SvgNodeIcons.component";

export function ContentSubmissionEnd({
	contentSubmissionFragmentRef,
	setIsModuleExpiredAndFailed,
}: ContentSubmissionEndProps) {
	const contentSubmission = useFragment<contentSubmissionEnd_ContentSubmissionFragment$key>(
		CONTENT_SUBMISSION_FRAGMENT,
		contentSubmissionFragmentRef,
	);

	const containedElementTypes =
		contentSubmission.learnOpportunity?.typeDefinition.containedElementTypes;

	const isModuleExpired =
		contentSubmission?.learnOpportunity?.root?.structureDefinition.extension?.configConsequences
			?.showAnswerExpired;

	const isTemporarilyBlockedUntil = isTemporarilyBlockedUntilUtil(
		contentSubmission.learnOpportunity?.typeDefinition.contentNodeAdvancementResult?.configResults,
	);

	const isPermanentlyBlocked = isPermanentlyBlockedUtil(
		contentSubmission.learnOpportunity?.typeDefinition.contentNodeAdvancementResult?.configResults,
	);
	const rewardingWasPossible = (
		rewardResults: ReadonlyArray<{
			readonly amount?: number;
			readonly certificateName?: string;
			readonly issuedCertificate?: {
				readonly fileRef: {
					readonly url: string | null;
				} | null;
			} | null;
			readonly kind: RewardResultKind;
			readonly pointsPoolName?: string;
			readonly rewardKind: RewardKind;
		}>,
	): boolean => {
		return (
			rewardResults.filter(
				(rewardResult) =>
					rewardResult.kind == "PointsRewardWasPossibleResult" ||
					rewardResult.kind == "CertificateRewardWasPossibleResult" ||
					rewardResult.kind == "EmailRewardWasPossibleResult",
			).length > 0
		);
	};

	const calculateResultPercentage = () => {
		if (
			contentSubmission.definition.relativeProgress?.numCorrect &&
			contentSubmission.definition.relativeProgress?.numElements
		) {
			return (
				(contentSubmission.definition.relativeProgress?.numCorrect /
					contentSubmission.definition.relativeProgress?.numElements) *
				100
			).toFixed(0);
		} else return 0;
	};
	const failedColor = "#CE1717";
	const noRewardsColor = "#787878";
	const selectKnobColor = () => {
		if (contentSubmission.definition.status === "failed") {
			return failedColor;
		} else if (
			contentSubmission.definition.status === "passed" &&
			!rewardingWasPossible(contentSubmission.definition.rewardResults ?? [])
		) {
			return noRewardsColor;
		} else if (contentSubmission.definition.status === "passed") {
			return LMS_WEBAPP_CONFIG_IMPL.brandColor;
		} else {
			return noRewardsColor;
		}
	};
	useEffect(() => {
		if (isModuleExpired && contentSubmission.definition.status === "failed") {
			setIsModuleExpiredAndFailed(true);
		}
	}, []);
	return (
		<ProjectContentElementContainer
			hideBack
			contentSubmissionFragmentRef={contentSubmission}
			nextButton={contentSubmission.learnOpportunity && <GoToDashboardButton />}
		>
			<div className="flex flex-column justify-content-center align-items-center flex-grow-1 px-5 pt-6">
				<div className="flex w-full justify-content-between align-items-center ">
					<div className="font-bold">Ergebnisse</div>
					{contentSubmission.learnOpportunity?.typeDefinition.extension?.configConsequences
						?.percentageRequiredToPass && (
						<span>
							Bestehensquote:{" "}
							{
								contentSubmission.learnOpportunity?.typeDefinition.extension?.configConsequences
									?.percentageRequiredToPass
							}
							%
						</span>
					)}
				</div>
				{contentSubmission.definition.relativeProgress && (
					<div className="relative flex justify-content-center align-items-center mb-3">
						<StyledKnob
							valueColor={selectKnobColor().toString()}
							min={0}
							max={100}
							strokeWidth={10}
							size={window.innerWidth < 480 ? 280 : 300}
							value={contentSubmission.definition.relativeProgress.percentageCorrect}
							showValue={false}
						/>
						<div className="absolute flex justify-content-center flex-column">
							<div className="text-center">
								<div className="text-6xl font-bold text-center mb-2">
									<Percentage color={selectKnobColor()}>{calculateResultPercentage()}%</Percentage>
								</div>
								<div className="flex w-full justify-content-center">
									<div style={{ width: "90%" }} className="text-base">
										{`${contentSubmission.definition.relativeProgress.numCorrect} von ${contentSubmission.definition.relativeProgress.numElements} Fragen korrekt
										beantwortet`}
									</div>
								</div>
							</div>
						</div>
					</div>
				)}

				{containedElementTypes?.includes("multipleChoice") &&
					(contentSubmission.definition.status === "passed" ? (
						<div className="mb-4">
							<Congratulation className="text-center">Herzlichen Glückwunsch!</Congratulation>
						</div>
					) : (
						<div className="mb-4">
							<Congratulation className="text-center">Schade!</Congratulation>
						</div>
					))}

				{containedElementTypes?.includes("multipleChoice") &&
					(contentSubmission.definition.status === "passed" ? (
						<ResultStatusText className="text-center">
							Sie haben{" "}
							<ResultStatusText className="font-bold">
								das Modul „{contentSubmission.learnOpportunity?.root?.structureDefinition.title}”
							</ResultStatusText>{" "}
							erfolgreich
							{contentSubmission.lastUpdated &&
								` am ${formatDateTime(contentSubmission.lastUpdated, false)} `}
							abgeschlossen!
						</ResultStatusText>
					) : (
						<ResultStatusText className="text-center">
							Sie haben{" "}
							<ResultStatusText className="font-bold">
								das Modul „{contentSubmission.learnOpportunity?.root?.structureDefinition.title}”
							</ResultStatusText>{" "}
							{contentSubmission.lastUpdated &&
								` am ${formatDateTime(contentSubmission.lastUpdated, false)} `}
							nicht bestanden!
						</ResultStatusText>
					))}

				{contentSubmission.definition.status === "passed" && (
					<Suspense fallback={<Fragment />}>
						<ModuleRating contentSubmissionFragmentRef={contentSubmission} />
					</Suspense>
				)}

				{isTemporarilyBlockedUntil && (
					<MessageContainer>{`
					Sie können das Modul am ${formatDateTimeAndAddDays(
						isTemporarilyBlockedUntil.blockedUntil as string,
						false,
						1,
					)} wiederholen.`}</MessageContainer>
				)}

				{isPermanentlyBlocked && (
					<MessageContainer>Sie haben keinen weiteren Versuch mehr.</MessageContainer>
				)}

				{rewardingWasPossible(contentSubmission.definition.rewardResults ?? []) && (
					<RewardsContainer className="w-12">
						{contentSubmission.definition.rewardResults
							?.filter((r) => ["Certificate"].includes(r.rewardKind))
							.map((reward, index) => (
								<div
									key={"reward-" + index}
									className={`border-black-alpha-90 border-round-sm border-1 flex mb-3 p-3 align-items-center justify-content-center`}
								>
									{reward.rewardKind === "Certificate" && (
										<a
											target="_blank"
											rel="noopener noreferrer"
											href={reward.issuedCertificate?.fileRef?.url || ""}
										>
											<div className=" flex text-xl justify-content-center align-items-center ">
												<RewardLabel className="mr-1">Zertifikat herunterladen</RewardLabel>
												<DownloadIcon heightIcon={20} widthIcon={20} />
											</div>
										</a>
									)}
								</div>
							))}
					</RewardsContainer>
				)}
				<div
					className={`w-full flex justify-content-center ${contentSubmission.definition.rewardResults?.length == 0 ? "mt-7" : "mt-0"}`}
				>
					{contentSubmission?.learnOpportunity?.root && (
						<ShowAnswerButtonContainer
							isContentSubmissionScreen={true}
							disabled={!isModuleExpired}
							learnOpportunityFragmentRef={contentSubmission.learnOpportunity.root}
							hasMultipleChoice={containedElementTypes?.includes("multipleChoice") || false}
						/>
					)}
				</div>
			</div>
		</ProjectContentElementContainer>
	);
}
